// Cart Drawer Promo Scroller JavaScript
// This file handles all promo scroller functionality

// ====================
// PROMO SCROLLER TRACKING
// ====================
// This tracks products added via promo scroller using Shopify cart attributes
// Data flows through to orders and can be exported from Shopify Admin → Orders

/**
 * Track promo scroller product addition by updating cart attributes
 * @param {Object} data - Product data (productId, productTitle, blockId, position, variantId)
 */
function trackPromoScrollerAddToCart(data) {
  const { productTitle, blockId, position, variantId } = data;
  
  if (!productTitle) return;
  
  // Fetch current cart to get existing attributes
  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      // Get existing promo scroller products from cart attributes
      const existingAttr = cart.attributes?.promo_scroller_products || '';
      
      // Determine position label (Featured = product 1, Scroller = products 2-5)
      const positionLabel = position || 'unknown';
      
      // Get variant info to check if on sale
      const cartItem = cart.items.find(item => item.variant_id == variantId);
      const onSale = cartItem && cartItem.compare_at_price && cartItem.compare_at_price > cartItem.price ? 'sale' : 'regular';
      
      // Create new entry: "Product Name (Block: ID|Position: X|Price: sale/regular)"
      const blockLabel = blockId ? ` (Block: ${blockId}|Position: ${positionLabel}|Price: ${onSale})` : '';
      const newEntry = `${productTitle}${blockLabel}`;
      
      // Build updated attribute value (comma-separated list)
      const updatedAttr = existingAttr 
        ? `${existingAttr}, ${newEntry}`
        : newEntry;
      
      // Update cart attributes (non-blocking, fire-and-forget)
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            promo_scroller_products: updatedAttr
          }
        })
      }).catch(() => {
        // Silently fail - don't interrupt user experience
      });
    })
    .catch(() => {
      // Silently fail if we can't fetch cart
    });
}

/**
 * Console helper: View promo scroller stats from localStorage
 * Usage: Run 'promoScrollerStats()' in browser console
 */
window.promoScrollerStats = function() {
  const stats = JSON.parse(localStorage.getItem('promo_scroller_stats') || '{}');
  const products = stats.products || {};
  
  console.log('=== PROMO SCROLLER STATISTICS ===');
  console.log('Total conversions:', stats.total || 0);
  console.log('\nTop Products:');
  
  // Sort products by conversion count
  const sorted = Object.entries(products)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 10);
  
  sorted.forEach(([name, data]) => {
    console.log(`  ${name}: ${data.count} conversions`);
  });
  
  console.log('\n💡 Tip: Export orders from Shopify Admin to see full monthly report');
  console.log('Filter by "promo_scroller_products" in order attributes');
};

/**
 * Store conversion data in localStorage for quick reference
 * (This is in addition to Shopify cart attributes)
 */
function storeLocalStats(data) {
  try {
    const stats = JSON.parse(localStorage.getItem('promo_scroller_stats') || '{}');
    
    // Initialize if needed
    if (!stats.products) stats.products = {};
    if (!stats.total) stats.total = 0;
    
    // Update product stats
    const productName = data.productTitle;
    if (!stats.products[productName]) {
      stats.products[productName] = { count: 0, lastAdded: null };
    }
    
    stats.products[productName].count++;
    stats.products[productName].lastAdded = new Date().toISOString();
    stats.total++;
    
    localStorage.setItem('promo_scroller_stats', JSON.stringify(stats));
  } catch (e) {
    // Silently fail if localStorage is unavailable
  }
}

// Global utility functions for variant selection
window.promoVariantFunctions = {
  /**
   * Format money for USD and CAD with proper thousand separators
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price string
   */
  formatMoney: function(cents) {
    // Get the base (shop) currency and customer's selected currency
    const shopCurrency = window.globalSpace?.shop_currency || 'USD';
    const activeCurrency = window.Shopify?.currency?.active || shopCurrency;

    // Check if currency conversion is needed
    const needsConversion = shopCurrency !== activeCurrency;

    // If conversion is needed and Currency converter is available
    if (needsConversion && typeof Currency !== 'undefined' && typeof Currency.convert === 'function') {
      try {
        // Convert from base currency to active currency
        const convertedCents = Currency.convert(cents, shopCurrency, activeCurrency);

        // Format manually to ensure correct currency display
        const dollars = convertedCents / 100;
        const formatted = this.addThousandSeparators(dollars);

        // Format with currency symbol
        if (activeCurrency === 'CAD') {
          return 'CAD $' + formatted;
        } else if (activeCurrency === 'USD') {
          return '$' + formatted;
        } else {
          return activeCurrency + ' ' + formatted;
        }
      } catch (e) {
        console.warn('[Promo Scroller] Currency conversion failed:', e);
        // Fall through to default formatting
      }
    }

    // Format manually using ACTIVE currency label (conversion happens elsewhere in theme)
    const dollars = cents / 100;
    const formatted = this.addThousandSeparators(dollars);

    // Always use the ACTIVE currency for display, not the base currency
    // Theme scripts will convert the number, we just need the right label
    if (activeCurrency === 'CAD') {
      return 'CAD $' + formatted;
    } else if (activeCurrency === 'USD') {
      return '$' + formatted;
    } else {
      // For other currencies, show the currency code
      return activeCurrency + ' ' + formatted;
    }
  },

  /**
   * Add thousand separators (commas) to number with 2 decimal places
   * @param {number} num - Number to format
   * @returns {string} Formatted number (e.g., "1,234.56")
   */
  addThousandSeparators: function(num) {
    const parts = num.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  },

  escapeHtml: function(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
};

/**
 * Find a variant that matches the selected options
 * Uses case-insensitive matching for reliability
 * @param {Object} productData - Product data from Shopify API
 * @param {Array} selectedOptions - Array of selected option values
 * @returns {Object|null} Matching variant or null
 */
function findMatchingVariant(productData, selectedOptions) {
  // Validate inputs
  if (!productData || !productData.variants || !selectedOptions) {
    console.error('[Variant Match] Invalid input data');
    return null;
  }

  // Normalize selected options for comparison
  const normalizedSelected = selectedOptions.map(opt =>
    opt?.toString().trim().toLowerCase()
  );

  // Find matching variant
  const variant = productData.variants.find(variant => {
    // Check option count matches
    if (variant.options.length !== normalizedSelected.length) {
      return false;
    }

    // Compare each option (case-insensitive)
    return variant.options.every((option, index) => {
      const variantOption = option?.toString().trim().toLowerCase();
      const selectedOption = normalizedSelected[index];

      return variantOption === selectedOption;
    });
  });

  return variant;
}

// At the top of the file, create a cleanup registry
window.promoScrollerCleanup = window.promoScrollerCleanup || {
  handlers: [],
  elements: []
};

// Create a smarter scroll utility
const CartScrollManager = {
  pendingScroll: null,
  attempts: 0,
  maxAttempts: 5,

  scrollToTop: function() {
    // Cancel any pending scroll
    this.cancel();

    // Reset attempt counter
    this.attempts = 0;

    // Start scroll with RAF
    this.attemptScroll();
  },

  attemptScroll: function() {
    // Check if we've exceeded max attempts
    if (this.attempts >= this.maxAttempts) {
      return;
    }

    // Find the scroll container (cache result for efficiency)
    if (!this.container || !document.body.contains(this.container)) {
      this.container = this.findScrollContainer();
    }

    if (!this.container) {
      return;
    }

    // Scroll to top
    const currentScroll = this.container.scrollTop;
    this.container.scrollTop = 0;

    // If scroll was successful or already at top, we're done
    if (currentScroll === 0 || this.container.scrollTop === 0) {
      return;
    }

    // Otherwise, try again after a frame
    this.attempts++;
    this.pendingScroll = requestAnimationFrame(() => {
      // Wait a bit, then try again
      setTimeout(() => this.attemptScroll(), 100 * this.attempts); // Exponential backoff
    });
  },

  findScrollContainer: function() {
    const selectors = [
      '.drawer-body',
      '.cart-drawer .drawer-body',
      '#component-cart-drawer .drawer-body',
      '.cart-drawer',
      '[data-cart-form]',
      '#component-cart-drawer'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element && element.scrollHeight > element.clientHeight) {
        return element;
      }
    }

    return null;
  },

  cancel: function() {
    if (this.pendingScroll) {
      cancelAnimationFrame(this.pendingScroll);
      this.pendingScroll = null;
      this.attempts = 0;
    }
  }
};

// Global promo scroller initialization function
window.initPromoScroller = function() {
  // STEP 1: Setup global event delegation ONCE if not already done
  // THIS MUST HAPPEN EVEN IF NO CONTAINERS EXIST YET (cart might be empty initially)
  if (!window.promoScrollerGlobalListenerAdded) {
    setupGlobalEventDelegation();
    window.promoScrollerGlobalListenerAdded = true;
  }

  // Find ALL promo containers (not just one)
  const promoContainers = document.querySelectorAll('.cart-drawer-promo-banner');
  if (promoContainers.length === 0) {
    // No containers to initialize, but event delegation is ready for when they appear
    return;
  }

  // STEP 2: Initialize each container individually
  promoContainers.forEach(function(promoContainer) {
    // Skip if already initialized
    if (promoContainer.hasAttribute('data-initialized')) {
      return;
    }

    // Mark as initialized
    promoContainer.setAttribute('data-initialized', 'true');
    
    // Force a reflow to ensure DOM is ready
    void promoContainer.offsetHeight;
    
    // Initialize container-specific features (scrolling, navigation)
    initializeContainerFeatures(promoContainer);
  });
};

// Setup global event delegation (called once)
function setupGlobalEventDelegation() {
  // STEP 4: Use ONLY delegated event handling (remove direct listeners)
  // Unified event delegation for all promo interactions
  const promoEventHandler = function(e) {
    const target = e.target;

    // Only process events from within the promo container
    const promoContainer = target.closest('.cart-drawer-promo-banner');
    if (!promoContainer) return;

    // Navigation buttons
    const navButton = target.closest('.promo-nav-prev') || target.closest('.promo-nav-next');
    if (navButton) {
      e.preventDefault();
      e.stopPropagation();
      handleNavigation(navButton);
      return false;
    }

    // Select variant buttons
    if (target.closest('.promo-select-variant-btn')) {
      e.preventDefault();
      e.stopPropagation();

      const btn = target.closest('.promo-select-variant-btn');
      const { productHandle, productId } = btn.dataset;

      if (productHandle && productId) {
        showInlineVariantOnCard(productHandle, productId, btn);
      }
      return false;
    }

    // Add to cart buttons
    if (target.closest('.promo-add-to-cart-btn')) {
      e.preventDefault();
      e.stopPropagation();

      const btn = target.closest('.promo-add-to-cart-btn');
      handleAddToCart(btn);
      return false;
    }
  };

  // Add the global event listener with capture phase
  document.addEventListener('click', promoEventHandler, true);
  
  // Also add in bubble phase as backup
  document.addEventListener('click', promoEventHandler, false);
}

// Global helper functions for event handlers

// Optimized add to cart handler
function handleAddToCart(btn) {
  const variantId = btn.dataset.variantId;
  const productId = btn.dataset.productId;
  const productTitle = btn.dataset.productTitle;
  const blockId = btn.dataset.blockId;
  const processingTime = btn.dataset.processingTime || null;
  
  if (!variantId) {
    return false;
  }
  
  // Prevent duplicate requests - check if already processing
  if (btn.classList.contains('loading') || btn.disabled) {
    return false;
  }
  
  // Disable button to prevent double-clicks
  btn.disabled = true;
  btn.classList.add('loading');
  let items =[];
  if (processingTime && processingTime != '' ) {
    items.push({ id: variantId, quantity: 1 , properties: { '_processing_time': processingTime } });
  } else {
    items.push({ id: variantId, quantity: 1 });
  }
  const formData = {
    items: items,
    sections: 'component-cart-drawer,header'
  };
  
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(formData)
  })
  .then(response => response.json())
  .then(dataJSON => {
    if (dataJSON.errors) {
      btn.classList.remove('success');
      btn.classList.remove('loading');
      btn.disabled = false;
      if (window.notificationEle) {
        window.notificationEle.updateNotification('Add to Cart', dataJSON.errors, { type: 'error', timeout: 5000 });
      }
      
     
        return;
    }
    sessionStorage.removeItem("NumberOnBack");
    document.querySelectorAll("[data-personalization-input]").forEach?.(el => el.value = "");
          BundleProduct.getEligibleBundleProductId().then(async (result) => {
          console.log(result, "result=================>>>>>>>>>>>>><<<<<<<<<");

          if (result) {
            const parentId = await BundleProduct.getCorrectCompleteBundleParent(
              result
            );

            if (parentId) {
              console.log("Final Parent Bundle to process:", parentId);
            }
          } else {
            console.log(" No matching product found");
          }
        });
    
    // Mark as success AFTER request completes
    btn.classList.add('success');
    
    // TRACKING: Track this promo scroller conversion
    // Determine position: check if button is in featured section or scroller
    const isFeatured = btn.closest('.promo-featured-card');
    const position = isFeatured ? 'featured' : 'scroller';
    
    trackPromoScrollerAddToCart({
      productId: productId,
      variantId: variantId,
      productTitle: productTitle,
      blockId: blockId,
      position: position,
      quantity: 1
    });
    
    // Store local stats for quick reference
    storeLocalStats({
      productTitle: productTitle,
      blockId: blockId
    });
    
    if (window.eventBus && window.PUB_SUB_EVENTS) {
      eventBus.publish(PUB_SUB_EVENTS.productATC, { data: { item: dataJSON.items[0] } });
    }
    
    const cartElement = document.querySelector('cart-form');
    if (cartElement && typeof cartElement._updateCart === 'function') {
      cartElement._updateCart(dataJSON.sections, btn);
    } else if (window.CartDrawer && typeof window.CartDrawer.refresh === 'function') {
      window.CartDrawer.refresh();
    }

    // Scroll to top using smart scroll manager
    CartScrollManager.scrollToTop();
  })
  .catch(() => {
    if (window.notificationEle) {
      window.notificationEle.updateNotification('Error', 'Failed to add product to cart', { type: 'error', timeout: 5000 });
    }
  })
  .finally(() => {
    btn.classList.remove('loading');
    btn.disabled = false;
  });
}

// Helper function for navigation logic
function handleNavigation(target) {
  const currentScrollContainer = target.closest('.cart-drawer-promo-banner')?.querySelector('[data-promo-scroll]');
  if (!currentScrollContainer) return false;
  
  const cards = currentScrollContainer.querySelectorAll('.promo-product-card');
  if (cards.length === 0) return false;
  
  // Determine current card index based on scroll position - find the closest card
  const scrollLeft = currentScrollContainer.scrollLeft;
  const containerPadding = parseFloat(getComputedStyle(currentScrollContainer).paddingLeft) || 0;
  
  // Find the card that's closest to the current scroll position
  let currentIndex = 0;
  let minDistance = Infinity;
  
  for (let i = 0; i < cards.length; i++) {
    const cardPosition = cards[i].offsetLeft - containerPadding;
    const distance = Math.abs(scrollLeft - cardPosition);
    
    if (distance < minDistance) {
      minDistance = distance;
      currentIndex = i;
    }
  }
  
  // Navigate to next/prev card
  const isNext = target.closest('.promo-nav-next') || target.classList.contains('promo-nav-next');
  const newIndex = isNext ? Math.min(currentIndex + 1, cards.length - 1) : Math.max(currentIndex - 1, 0);
  
  if (newIndex !== currentIndex) {
    const targetCard = cards[newIndex];
    const scrollToPosition = targetCard.offsetLeft - containerPadding;
    currentScrollContainer.scrollTo({ left: scrollToPosition, behavior: 'smooth' });
  }
  return true;
}

// Show variant selector inline on the product card
async function showInlineVariantOnCard(productHandle, productId, selectBtn) {
  try {
    const response = await fetch('/products/' + productHandle + '.js');
    const productData = await response.json();
    
    if (!productData || !productData.variants || productData.variants.length <= 1) {
      return;
    }
    
    // Find the variant wrapper and button in the card
    const card = selectBtn.closest('.promo-product-card');
    const variantWrapper = card.querySelector('.promo-variant-selector-wrapper[data-product-id="' + productId + '"]');
    
    if (!variantWrapper) {
      return;
    }
    
    // Check if already built (has content)
    const isBuilt = variantWrapper.querySelector('.promo-selected-variant-id');
    
    if (!isBuilt) {
      window.buildInlineVariantOptions(productData, variantWrapper, selectBtn);
      variantWrapper.style.display = 'block';
      
      // Hide title and description (image stays visible behind overlay)
      const productTitle = card.querySelector('.promo-product-title');
      const productDesc = card.querySelector('.promo-product-description');
      
      // Mark card as showing variants for overlay styling
      card.setAttribute('data-variants-shown', 'true');
      
      if (productTitle) {
        productTitle.style.display = 'none';
        productTitle.setAttribute('data-hidden-for-variants', 'true');
      }
      if (productDesc) {
        productDesc.style.display = 'none';
        productDesc.setAttribute('data-hidden-for-variants', 'true');
      }
    }
  } catch (error) {
    console.error('Error loading product variants:', error);
  }
}

// Initialize container-specific features
function initializeContainerFeatures(promoContainer) {
  // Force DOM reflow to ensure all elements are rendered
  promoContainer.offsetHeight;
  
  const scrollContainer = promoContainer.querySelector('[data-promo-scroll]');
  const isThreeProducts = promoContainer.classList.contains('promo-banner-three-products');
  
  if (!scrollContainer) {
    return;
  }
  
  // Cache navigation elements
  const prevBtn = promoContainer.querySelector('.promo-nav-prev');
  const nextBtn = promoContainer.querySelector('.promo-nav-next');
  
  // Pre-calculate scroll amount
  let scrollAmount = 200; // default fallback
  
  // Debounce function for expensive operations
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  // Use RAF for smooth animations
  function smoothUpdate(callback) {
    requestAnimationFrame(callback);
  }

  // Initialize scroll amount calculation with smart positioning
  function calculateScrollAmount() {
    const firstCard = scrollContainer.querySelector('.promo-product-card');
    if (firstCard) {
      // Get precise measurements
      const containerWidth = scrollContainer.clientWidth;
      const scrollPadding = parseFloat(getComputedStyle(scrollContainer).paddingLeft) || 0;
      const availableWidth = containerWidth - (scrollPadding * 2);
      const cardWidth = firstCard.offsetWidth;
      const gap = 15; // matches CSS --promo-gap
      
      // Calculate optimal scroll distance for card-by-card navigation
      scrollAmount = cardWidth + gap;
    } else {
      scrollAmount = 200; // fallback
    }
  }
  
  // Optional navigation functionality (only if buttons exist and not 3 products)
  if (prevBtn && nextBtn && !isThreeProducts) {
    calculateScrollAmount(); // Initial calculation
    
    function updateNavButtons() {
      smoothUpdate(() => {
        const isAtStart = scrollContainer.scrollLeft <= 0;
        const isAtEnd = scrollContainer.scrollLeft >= (scrollContainer.scrollWidth - scrollContainer.clientWidth - 1);
        
        prevBtn.disabled = isAtStart;
        nextBtn.disabled = isAtEnd;
      });
    }
    
    // Navigation is now handled by global event delegation
    // No need for direct onclick handlers that could conflict
    
    // Use debounced scroll listener
    scrollContainer.addEventListener('scroll', debounce(updateNavButtons, 100));
    
    // Initial state - delay to ensure DOM is ready
    setTimeout(updateNavButtons, 100);
  }
  
  // Add touch/swipe functionality for mobile and desktop (only if not 3 products)
  if (!isThreeProducts) {
    let isDown = false;
    let startX;
    let scrollLeft;
    
    // Mouse events for desktop
    scrollContainer.addEventListener('mousedown', (e) => {
      isDown = true;
      scrollContainer.style.cursor = 'grabbing';
      startX = e.pageX - scrollContainer.offsetLeft;
      scrollLeft = scrollContainer.scrollLeft;
      e.preventDefault();
    });
  
    scrollContainer.addEventListener('mouseleave', () => {
      isDown = false;
      scrollContainer.style.cursor = 'grab';
    });
    
    scrollContainer.addEventListener('mouseup', () => {
      isDown = false;
      scrollContainer.style.cursor = 'grab';
    });
    
    scrollContainer.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - scrollContainer.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier
      scrollContainer.scrollLeft = scrollLeft - walk;
    });
    
    // Touch events for mobile
    let touchStartX = 0;
    let touchScrollLeft = 0;
    
    scrollContainer.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchScrollLeft = scrollContainer.scrollLeft;
    }, { passive: true });
    
    scrollContainer.addEventListener('touchmove', (e) => {
      if (!touchStartX) return;
      const touchX = e.touches[0].clientX;
      const walk = (touchStartX - touchX) * 1.5; // Touch scroll speed
      scrollContainer.scrollLeft = touchScrollLeft + walk;
    }, { passive: true });
    
    scrollContainer.addEventListener('touchend', () => {
      touchStartX = 0;
    }, { passive: true });
    
    // Set grab cursor for desktop
    scrollContainer.style.cursor = 'grab';
    
    // Recalculate scroll amount on window resize
    window.addEventListener('resize', debounce(calculateScrollAmount, 250));
  }
  
  // Build inline variant options matching product page style (make it global)
  window.buildInlineVariantOptions = function(productData, container, selectBtn) {
    container.innerHTML = '';
    
    // Generate unique ID for this product's inputs
    const uniqueId = 'prod-' + productData.id;
    container.setAttribute('data-unique-id', uniqueId);
    
    // Create hidden input for selected variant ID
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.className = 'promo-selected-variant-id';
    hiddenInput.value = productData.variants[0].id;
    container.appendChild(hiddenInput);
    
    if (productData.options && productData.options.length > 0) {
      productData.options.forEach((option, optionIndex) => {
        
        // Always create option groups, even if only 1 value (needed for variant matching)
        if (option.values && option.values.length >= 1) {
          const optionDiv = createInlineOptionGroup(option, optionIndex, productData, container, uniqueId);
          container.appendChild(optionDiv);
        }
      });
    }
    
    setupInlineVariantChangeLogic(productData, container, selectBtn, uniqueId);
  };
  
  // Create option group for inline display
  function createInlineOptionGroup(option, optionIndex, productData, container, uniqueId) {
    const optionDiv = document.createElement('div');
    optionDiv.className = 'promo-inline-option-group';
    optionDiv.style.cssText = 'margin-bottom: 10px;';
    
    // If only 1 value, create hidden input (don't show it visually)
    if (option.values.length === 1) {
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'radio';
      hiddenInput.className = 'promo-variant-input';
      hiddenInput.name = uniqueId + '-option-' + optionIndex;
      hiddenInput.value = option.values[0];
      hiddenInput.checked = true;
      hiddenInput.style.display = 'none';
      optionDiv.appendChild(hiddenInput);
      optionDiv.style.display = 'none'; // Hide the entire option group
      return optionDiv;
    }
    
    // Handle Size option with Youth/Adult grid layout
    if (option.name.toLowerCase() === 'size') {
      createInlineSizeGrid(option, optionIndex, optionDiv, uniqueId);
    } else if (option.name.toLowerCase() === 'color') {
      createInlineColorSwatches(option, optionIndex, optionDiv, uniqueId);
    } else {
      createInlineStandardOptions(option, optionIndex, optionDiv, uniqueId);
    }
    
    return optionDiv;
  }
  
  // Create size grid with Youth/Adult rows (matching product page)
  function createInlineSizeGrid(option, optionIndex, container, uniqueId) {
    // Check for simple sizes
    let hasSimpleSizes = false;
    option.values.forEach(value => {
      const firstChar = value.charAt(0);
      if (value === 'YOUTH' || value === 'ADULT' || value === 'Youth' || value === 'Adult') {
        hasSimpleSizes = true;
      } else if (firstChar !== 'A' && firstChar !== 'Y') {
        hasSimpleSizes = true;
      }
    });
    
    if (hasSimpleSizes) {
      // Simple sizes layout
      const ul = document.createElement('ul');
      ul.style.cssText = 'list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 6px;';
      
      option.values.forEach((value, valueIndex) => {
        const li = createInlineSizeButton(value, optionIndex, valueIndex, uniqueId);
        ul.appendChild(li);
      });
      
      container.appendChild(ul);
    } else {
      // Complex grid layout with Youth/Adult rows
      const adultSizes = [];
      const youthSizes = [];
      
      option.values.forEach(value => {
        const firstChar = value.charAt(0);
        if (firstChar === 'A') {
          adultSizes.push(value);
        } else if (firstChar === 'Y') {
          youthSizes.push(value);
        }
      });
      
      if (youthSizes.length > 0) {
        const youthRow = document.createElement('div');
        youthRow.className = 'promo-size-row';
        
        const youthLabel = document.createElement('span');
        youthLabel.className = 'size-group-label';
        youthLabel.textContent = 'Youth';
        
        const youthUl = document.createElement('ul');
        
        youthSizes.forEach((value) => {
          const li = createInlineSizeButton(value, optionIndex, option.values.indexOf(value), uniqueId);
          youthUl.appendChild(li);
        });
        
        youthRow.appendChild(youthLabel);
        youthRow.appendChild(youthUl);
        container.appendChild(youthRow);
      }
      
      if (adultSizes.length > 0) {
        const adultRow = document.createElement('div');
        adultRow.className = 'promo-size-row';
        
        const adultLabel = document.createElement('span');
        adultLabel.className = 'size-group-label';
        adultLabel.textContent = 'Adult';
        
        const adultUl = document.createElement('ul');
        
        adultSizes.forEach((value) => {
          const li = createInlineSizeButton(value, optionIndex, option.values.indexOf(value), uniqueId);
          adultUl.appendChild(li);
        });
        
        adultRow.appendChild(adultLabel);
        adultRow.appendChild(adultUl);
        container.appendChild(adultRow);
      }
    }
  }
  
  // Create individual size button for inline display
  function createInlineSizeButton(value, optionIndex, valueIndex, uniqueId) {
    const li = document.createElement('li');
    
    const input = document.createElement('input');
    input.type = 'radio';
    input.className = 'promo-variant-input';
    input.name = uniqueId + '-option-' + optionIndex;
    input.value = value;
    input.id = uniqueId + '-inline-' + optionIndex + '-' + valueIndex;
    input.setAttribute('data-option-index', optionIndex);
    input.style.display = 'none';
    if (valueIndex === 0) input.checked = true;
    
    const label = document.createElement('label');
    label.className = 'swatch-label';
    label.setAttribute('for', input.id);
    
    // Display value without prefix for Youth/Adult sizes
    let displayValue = value;
    if (value.charAt(0) === 'Y' && value !== 'YOUTH' && value !== 'Youth') {
      displayValue = value.substring(1);
    } else if (value.charAt(0) === 'A' && value !== 'ADULT' && value !== 'Adult') {
      displayValue = value.substring(1);
    }
    label.textContent = displayValue;
    
    li.appendChild(input);
    li.appendChild(label);
    return li;
  }
  
  // Create color swatches for inline display (with label like Youth/Adult)
  function createInlineColorSwatches(option, optionIndex, container, uniqueId) {
    const colorRow = document.createElement('div');
    colorRow.className = 'promo-size-row';
    
    const colorLabel = document.createElement('span');
    colorLabel.className = 'size-group-label';
    colorLabel.textContent = 'Color';
    
    const ul = document.createElement('ul');
    
    option.values.forEach((value, valueIndex) => {
      const li = document.createElement('li');
      
      const input = document.createElement('input');
      input.type = 'radio';
      input.className = 'promo-variant-input';
      input.name = uniqueId + '-option-' + optionIndex;
      input.value = value;
      input.id = uniqueId + '-inline-' + optionIndex + '-' + valueIndex;
      input.setAttribute('data-option-index', optionIndex);
      input.style.display = 'none';
      if (valueIndex === 0) input.checked = true;
      
      const label = document.createElement('label');
      label.className = 'swatch-label color-swatch-label';
      label.setAttribute('for', input.id);
      label.setAttribute('title', value); // Add tooltip with color name
      
      // Get background color - try common color names first
      let backgroundColor = getSwatchColor(value);
      label.style.cssText = backgroundColor;
      
      li.appendChild(input);
      li.appendChild(label);
      ul.appendChild(li);
    });
    
    colorRow.appendChild(colorLabel);
    colorRow.appendChild(ul);
    container.appendChild(colorRow);
  }
  
  // Helper function to get swatch color from color name
  function getSwatchColor(colorName) {
    // Try to get color map from the JSON script tag (loaded globally via json-swatches-colorlist snippet)
    let colorMap = {};

    try {
      const colorScript = document.querySelector('script[data-swatches-colorlist-json]');
      if (colorScript && colorScript.textContent) {
        colorMap = JSON.parse(colorScript.textContent);
      }
    } catch (e) {
      console.warn('[Promo Scroller] Failed to load color swatches from JSON:', e);
    }

    // Normalize color name for lookup (remove spaces, lowercase)
    const normalizedColor = colorName.toLowerCase().trim().replace(/\s+/g, '');

    // Direct match in color map
    if (colorMap[normalizedColor]) {
      const colorValue = colorMap[normalizedColor];

      // Check if it's an image URL or hex code
      if (colorValue.startsWith('http://') || colorValue.startsWith('https://')) {
        // Image URL - use background-image
        return 'background-image: url(' + colorValue + '); background-size: cover; background-position: center;';
      } else {
        // Hex code - use background-color
        // Add border for white/light colors to make them visible
        if (normalizedColor.includes('white') || colorValue === '#ffffff' || colorValue === '#fff') {
          return 'background-color: ' + colorValue + '; border: 2px solid #d1d5db !important;';
        }
        return 'background-color: ' + colorValue + ';';
      }
    }

    // Try partial match (for multi-word colors like "Royal Blue" matching "royal")
    for (const key in colorMap) {
      if (normalizedColor.includes(key)) {
        const colorValue = colorMap[key];

        if (colorValue.startsWith('http://') || colorValue.startsWith('https://')) {
          return 'background-image: url(' + colorValue + '); background-size: cover; background-position: center;';
        } else {
          if (key.includes('white') || colorValue === '#ffffff' || colorValue === '#fff') {
            return 'background-color: ' + colorValue + '; border: 2px solid #d1d5db !important;';
          }
          return 'background-color: ' + colorValue + ';';
        }
      }
    }

    // Fallback: try to use the color name as-is
    if (normalizedColor.includes('white')) {
      return 'background-color: #ffffff; border: 2px solid #d1d5db !important;';
    }

    return 'background-color: ' + normalizedColor + ';';
  }
  
  // Create standard options for inline display
  function createInlineStandardOptions(option, optionIndex, container, uniqueId) {
    const ul = document.createElement('ul');
    ul.style.cssText = 'list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 6px;';
    
    option.values.forEach((value, valueIndex) => {
      const li = createInlineSizeButton(value, optionIndex, valueIndex, uniqueId);
      ul.appendChild(li);
    });
    
    container.appendChild(ul);
  }
  
  // Setup variant change logic for inline selector
  function setupInlineVariantChangeLogic(productData, container, selectBtn, uniqueId) {
    const variantInputs = container.querySelectorAll('input.promo-variant-input');
    const variantIdInput = container.querySelector('.promo-selected-variant-id');
    const card = container.closest('.promo-product-card');
    
    function updateSelectedVariant() {
      const selectedOptions = [];
      
      productData.options.forEach((option, index) => {
        const selectedInput = container.querySelector('input[name="' + uniqueId + '-option-' + index + '"]:checked');
        if (selectedInput) {
          selectedOptions[index] = selectedInput.value;
        }
      });

      // Find matching variant using case-insensitive matching
      const selectedVariant = findMatchingVariant(productData, selectedOptions);
      
      if (selectedVariant) {
        variantIdInput.value = selectedVariant.id;
        selectBtn.dataset.variantId = selectedVariant.id;
        selectBtn.dataset.productId = productData.id;
        
        // Change button to "Add to Cart" when variant is selected
        if (selectedVariant.available) {
          const priceFormatted = window.promoVariantFunctions.formatMoney(selectedVariant.price);
          selectBtn.textContent = 'Add to Cart - ' + priceFormatted;
          
          // Remove old classes and add new one
          selectBtn.classList.remove('promo-select-variant-btn');
          selectBtn.classList.add('promo-add-to-cart-btn');
          selectBtn.disabled = false;
          
          // Remove any existing click handlers to prevent duplicates
          if (selectBtn._addToCartHandler) {
            selectBtn.removeEventListener('click', selectBtn._addToCartHandler);
            selectBtn.removeEventListener('mousedown', selectBtn._addToCartHandler);
            selectBtn.removeEventListener('touchstart', selectBtn._addToCartHandler);
          }
          
          // Create new click handler that adds to cart
          selectBtn._addToCartHandler = function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            const currentVariantId = this.dataset.variantId;
            
            if (currentVariantId) {
              handleAddToCart(this);
            }
            
            return false;
          };
          
          // Attach the new handler
          selectBtn.addEventListener('click', selectBtn._addToCartHandler, true);
          selectBtn.addEventListener('mousedown', selectBtn._addToCartHandler, true);
          selectBtn.addEventListener('touchstart', selectBtn._addToCartHandler, { passive: false });
          
        } else {
          selectBtn.textContent = 'Sold Out';
          selectBtn.disabled = true;
        }
      }
    }
    
    variantInputs.forEach((input) => {
      input.addEventListener('change', function() {
        updateSelectedVariant();
      });
    });
    
    updateSelectedVariant();
  }
  
  // OLD FUNCTION - Keep for backwards compatibility but won't be called
  function showInlineVariantSelection(productHandle, productId, productTitle) {
    const liveBanner = document.querySelector('.cart-drawer-promo-banner');
    if (!liveBanner) return;
    
    // Store current scroll position to maintain it
    const cartDrawer = document.querySelector('.cart-drawer') || document.querySelector('[data-cart-form]') || document.body;
    window.cartScrollPosition = cartDrawer.scrollTop;
    
    // Step 1: Smoothly hide scroller navigation and products scroll (keep product 1 visible)
    const scrollerNav = liveBanner.querySelector('.promo-scroller-nav-controls');
    const productsScroll = liveBanner.querySelector('.promo-products-scroll');
    
    if (scrollerNav) {
      scrollerNav.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      scrollerNav.style.opacity = '0';
      scrollerNav.style.transform = 'translateY(-10px)';
      setTimeout(() => scrollerNav.style.display = 'none', 300);
    }
    
    if (productsScroll) {
      productsScroll.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      productsScroll.style.opacity = '0';
      productsScroll.style.transform = 'translateY(-10px)';
      setTimeout(() => productsScroll.style.display = 'none', 300);
    }
    
    // Step 2: Create variant selection container as a card
    const variantContainer = document.createElement('div');
    variantContainer.id = 'promo-variant-interface';
    variantContainer.style.cssText = 'background: var(--pe-color-gray-100); border: 1px solid var(--pe-border-color); border-radius: 8px; padding: 0; margin: 15px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); opacity: 0; transform: translateY(20px); transition: opacity 0.4s ease, transform 0.4s ease; position: relative;';
    
    // Show loading state first
    variantContainer.innerHTML = '<div style="text-align: center; padding: 40px;"><div class="promo-loading-spinner" style="display: inline-block; width: 20px; height: 20px; border: 2px solid var(--pe-color-gray-200); border-radius: 50%; border-top-color: var(--pe-color-primary); animation: spin 1s linear infinite;"></div><div style="margin-top: 10px; color: var(--pe-color-gray-600);">Loading options...</div></div>';
    
    // Add enhanced CSS animations
    if (!document.querySelector('#promo-smooth-styles')) {
      const smoothStyles = document.createElement('style');
      smoothStyles.id = 'promo-smooth-styles';
      smoothStyles.textContent = `
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeOutDown { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-20px); } }
        .promo-variant-smooth-enter { animation: fadeInUp 0.4s ease forwards; }
        .promo-variant-smooth-exit { animation: fadeOutDown 0.3s ease forwards; }
      `;
      document.head.appendChild(smoothStyles);
    }
    
    // Insert it directly inside the promo banner
    liveBanner.appendChild(variantContainer);
    
    // Trigger smooth entrance animation
    setTimeout(() => {
      variantContainer.style.opacity = '1';
      variantContainer.style.transform = 'translateY(0)';
    }, 100);
    
    // Store reference for cleanup
    window.currentVariantContainer = variantContainer;
    window.currentLiveBanner = liveBanner;
    
    // Load the actual product variants
    loadVariantInterface(productHandle, productId, productTitle, variantContainer);
    
    // Restore scroll position after a brief delay
    setTimeout(() => {
      if (window.cartScrollPosition !== undefined) {
        cartDrawer.scrollTop = window.cartScrollPosition;
      }
    }, 150);
  }
  
  // Load and build variant interface
  async function loadVariantInterface(productHandle, productId, productTitle, container) {
    try {
      const response = await fetch('/products/' + productHandle + '.js');
      const productData = await response.json();
      
      if (!productData || !productData.variants || productData.variants.length <= 1) {
        window.hideSimpleVariantSelection();
        return;
      }
      
      buildVariantInterface(productData, container);
      
    } catch (error) {
      container.style.opacity = '0';
      container.innerHTML = '<div style="text-align: center; padding: 40px; color: var(--pe-color-danger);">Error loading product options. Please try again.</div>';
      setTimeout(() => container.style.opacity = '1', 100);
    }
  }
  
  // Build variant interface with product page styling (image left, content right)
  function buildVariantInterface(productData, container) {
    const currentVariant = productData.variants[0];
    
    // Create the interface HTML structure with smooth transition
    container.style.opacity = '0';
    container.innerHTML = '';
    
    // Close X button positioned in top-right corner of card
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button'; // Explicitly set button type to prevent form submission
    closeBtn.style.cssText = 'position: absolute; top: 12px; right: 12px; background: var(--pe-color-white); border: 1px solid var(--pe-border-color); color: var(--pe-color-gray-600); font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; transition: all 0.2s ease; z-index: 10; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);';
    closeBtn.innerHTML = '×';
    closeBtn.setAttribute('aria-label', 'Close variant selection');
    
    // Prevent any default behavior and stop event propagation
    closeBtn.addEventListener('click', function(event) {
      event.preventDefault();
      event.stopPropagation();
      window.hideSimpleVariantSelection();
      return false;
    });
    
    // Add hover effect
    closeBtn.addEventListener('mouseenter', function() {
      this.style.backgroundColor = 'var(--pe-color-gray-100)';
      this.style.borderColor = 'var(--pe-color-gray-400)';
      this.style.color = 'var(--pe-color-gray-700)';
      this.style.transform = 'scale(1.05)';
    });
    closeBtn.addEventListener('mouseleave', function() {
      this.style.backgroundColor = 'var(--pe-color-white)';
      this.style.borderColor = 'var(--pe-border-color)';
      this.style.color = 'var(--pe-color-gray-600)';
      this.style.transform = 'scale(1)';
    });
    
    container.appendChild(closeBtn);
    
    // Card content wrapper with padding
    const cardContent = document.createElement('div');
    cardContent.style.cssText = 'padding: 20px; padding-top: 40px;'; // Extra top padding for close button
    
    // Product page layout: Image left, content right
    const productLayout = document.createElement('div');
    productLayout.style.cssText = 'display: flex; gap: 20px; margin-bottom: 20px;';
    
    // Product image (left side - larger)
    const imageDiv = document.createElement('div');
    imageDiv.style.cssText = 'flex: 0 0 120px; width: 120px; height: 120px; border-radius: 8px; overflow: hidden; background: var(--pe-color-gray-200);';
    
    if (productData.featured_image) {
      const img = document.createElement('img');
      // Add size parameter for higher quality image
      img.src = productData.featured_image.replace(/(\.[^.]+)$/, '_400x400$1');
      img.alt = productData.title;
      img.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
      imageDiv.appendChild(img);
    }
    
    // Product content (right side) - no title, no separate price
    const contentDiv = document.createElement('div');
    contentDiv.style.cssText = 'flex: 1; display: flex; flex-direction: column;';
    
    // Hidden price element for tracking (no display)
    const priceElement = document.createElement('span');
    priceElement.id = 'variant-price-display';
    priceElement.style.cssText = 'display: none;';
    priceElement.textContent = window.promoVariantFunctions.formatMoney(currentVariant.price);
    
    // Variant options form
    const form = document.createElement('form');
    form.id = 'variant-selection-form';
    form.style.cssText = 'flex: 1; display: flex; flex-direction: column;';
    
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.id = 'selected-variant-id';
    hiddenInput.value = currentVariant.id;
    form.appendChild(hiddenInput);
    
    // Build option groups (matching product page style)
    if (productData.options && productData.options.length > 0) {
      productData.options.forEach((option, optionIndex) => {
        if (option.values && option.values.length > 1) {
          const optionGroup = createOptionGroup(option, optionIndex, productData);
          form.appendChild(optionGroup);
        } else if (option.values && option.values.length === 1) {
          // Create hidden input for single-value options to maintain proper indexing
          const hiddenOptionInput = document.createElement('input');
          hiddenOptionInput.type = 'hidden';
          hiddenOptionInput.name = 'option-' + optionIndex;
          hiddenOptionInput.value = option.values[0];
          hiddenOptionInput.setAttribute('data-option-index', optionIndex);
          form.appendChild(hiddenOptionInput);
        }
      });
    }
    
    // Add to cart button with price
    const addToCartBtn = document.createElement('button');
    addToCartBtn.type = 'button';
    addToCartBtn.id = 'variant-add-to-cart';
    addToCartBtn.style.cssText = 'width: 100%; background: var(--pe-color-primary); color: var(--pe-color-white); border: none; padding: 14px 24px; border-radius: 4px; font-size: 16px; font-weight: 600; cursor: pointer; margin-top: auto; transition: background-color 0.2s ease;';
    const currentPrice = window.promoVariantFunctions.formatMoney(currentVariant.price);
    addToCartBtn.textContent = currentVariant.available ? 'Add to Cart - ' + currentPrice : 'Sold Out';
    addToCartBtn.disabled = !currentVariant.available;
    
    // Add event listener for add to cart - get current variant ID when clicked
    addToCartBtn.addEventListener('click', () => {
      const currentVariantId = document.getElementById('selected-variant-id')?.value || hiddenInput.value;
      addSelectedVariantToCart(currentVariantId);
    });
    
    form.appendChild(addToCartBtn);
    
    // Assemble the content (no title, just form)
    contentDiv.appendChild(priceElement);
    contentDiv.appendChild(form);
    
    productLayout.appendChild(imageDiv);
    productLayout.appendChild(contentDiv);
    cardContent.appendChild(productLayout);
    container.appendChild(cardContent);
    
    // Setup variant change logic
    setupVariantChangeLogic(productData, container);
    
    // Smooth fade-in of the complete interface
    setTimeout(() => container.style.opacity = '1', 150);
  }
  
  // Create option group (matching exact product page styling)
  function createOptionGroup(option, optionIndex, productData) {
    const fieldset = document.createElement('fieldset');
    fieldset.className = 'product-form__input';
    fieldset.style.cssText = 'border: 0; padding: 0; margin: 0; padding-bottom: 20px; margin-bottom: 20px; border-bottom: 1px solid var(--pe-border-color);';
    fieldset.setAttribute('data-optionindex', optionIndex + 1);
    
    // No variant label - just the options directly
    
    // Handle Size option with Youth/Adult grid layout
    if (option.name.toLowerCase() === 'size') {
      createSizeGrid(option, optionIndex, fieldset);
    } else if (option.name.toLowerCase() === 'color') {
      createColorSwatches(option, optionIndex, fieldset);
    } else {
      createStandardOptions(option, optionIndex, fieldset);
    }
    
    return fieldset;
  }
  
  // Create size grid with Youth/Adult rows (matching product page logic exactly)
  function createSizeGrid(option, optionIndex, fieldset) {
    // Check for simple sizes (matching product page logic)
    let hasSimpleSizes = false;
    
    option.values.forEach(value => {
      const firstChar = value.charAt(0);
      if (value === 'YOUTH' || value === 'ADULT' || value === 'Youth' || value === 'Adult') {
        hasSimpleSizes = true;
      } else if (firstChar !== 'A' && firstChar !== 'Y') {
        hasSimpleSizes = true;
      }
    });
    
    if (hasSimpleSizes) {
      // Simple sizes layout (like Youth, Adult, S, M, L, etc.)
      const ul = document.createElement('ul');
      ul.className = 'list-unstyled d-flex flex-wrap my-0 py-0 gap-2';
      ul.style.cssText = 'list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 8px;';
      
      option.values.forEach((value, valueIndex) => {
        const li = createSizeButton(value, optionIndex, valueIndex, option.values);
        ul.appendChild(li);
      });
      
      fieldset.appendChild(ul);
    } else {
      // Complex grid layout with Youth/Adult rows (AS, AM, YS, YM, etc.)
      const adultSizes = [];
      const youthSizes = [];
      
      option.values.forEach(value => {
        const firstChar = value.charAt(0);
        if (firstChar === 'A') {
          adultSizes.push(value);
        } else if (firstChar === 'Y') {
          youthSizes.push(value);
        }
      });
      
      if (youthSizes.length > 0) {
        const youthRow = document.createElement('div');
        youthRow.className = 'd-flex gap-2 align-items-start mb-2';
        youthRow.style.cssText = 'display: flex; gap: 8px; align-items: start; margin-bottom: 8px;';
        
        const youthLabel = document.createElement('span');
        youthLabel.className = 'size-group-label fs-sm';
        youthLabel.style.cssText = 'font-size: 14px; color: var(--pe-color-gray-600); min-width: 40px;';
        youthLabel.textContent = 'Youth';
        
        const youthUl = document.createElement('ul');
        youthUl.className = 'list-unstyled d-flex flex-wrap my-0 py-0 gap-2';
        youthUl.style.cssText = 'list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 8px;';
        
        youthSizes.forEach((value, valueIndex) => {
          const li = createSizeButton(value, optionIndex, option.values.indexOf(value), option.values);
          youthUl.appendChild(li);
        });
        
        youthRow.appendChild(youthLabel);
        youthRow.appendChild(youthUl);
        fieldset.appendChild(youthRow);
      }
      
      if (adultSizes.length > 0) {
        const adultRow = document.createElement('div');
        adultRow.className = 'd-flex gap-2 align-items-start mb-2';
        adultRow.style.cssText = 'display: flex; gap: 8px; align-items: start; margin-bottom: 8px;';
        
        const adultLabel = document.createElement('span');
        adultLabel.className = 'size-group-label fs-sm';
        adultLabel.style.cssText = 'font-size: 14px; color: var(--pe-color-gray-600); min-width: 40px;';
        adultLabel.textContent = 'Adult';
        
        const adultUl = document.createElement('ul');
        adultUl.className = 'list-unstyled d-flex flex-wrap my-0 py-0 gap-2';
        adultUl.style.cssText = 'list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 8px;';
        
        adultSizes.forEach((value, valueIndex) => {
          const li = createSizeButton(value, optionIndex, option.values.indexOf(value), option.values);
          adultUl.appendChild(li);
        });
        
        adultRow.appendChild(adultLabel);
        adultRow.appendChild(adultUl);
        fieldset.appendChild(adultRow);
      }
    }
  }
  
  // Create individual size button (matching swatch styling)
  function createSizeButton(value, optionIndex, valueIndex, allValues) {
    const li = document.createElement('li');
    li.className = 'swatch';
    
    const input = document.createElement('input');
    input.type = 'radio';
    input.className = 'form-control variant_selector';
    input.name = 'option-' + optionIndex;
    input.value = value;
    input.id = 'promo-option-' + optionIndex + '-' + valueIndex;
    input.setAttribute('data-option-index', optionIndex);
    if (valueIndex === 0) input.checked = true;
    
    const label = document.createElement('label');
    label.className = 'form-label swatch-label';
    label.setAttribute('for', input.id);
    label.style.cssText = 'border-radius: 2px; border: 1px solid var(--pe-border-color); background-color: var(--pe-color-white); padding: 2px 10px; min-width: 36px; min-height: 36px; text-align: center; line-height: 1.2; transition: all 0.2s ease-out; display: flex; align-items: center; justify-content: center; cursor: pointer; margin: 0;';
    label.textContent = value;
    
    // Style for checked state
    if (valueIndex === 0) {
      label.style.backgroundColor = 'var(--pe-color-primary)';
      label.style.color = 'var(--pe-color-white)';
      label.style.borderColor = 'var(--pe-color-primary)';
    }
    
    li.appendChild(input);
    li.appendChild(label);
    return li;
  }
  
  // Create color swatches (if needed)
  function createColorSwatches(option, optionIndex, fieldset) {
    const ul = document.createElement('ul');
    ul.className = 'list-unstyled d-flex flex-wrap my-0 py-0 gap-2';
    ul.style.cssText = 'list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 8px;';

    option.values.forEach((value, valueIndex) => {
      const li = document.createElement('li');
      li.className = 'swatch color-swatch';

      const input = document.createElement('input');
      input.type = 'radio';
      input.className = 'form-control variant_selector';
      input.name = 'option-' + optionIndex;
      input.value = value;
      input.id = 'promo-option-' + optionIndex + '-' + valueIndex;
      input.setAttribute('data-option-index', optionIndex);
      if (valueIndex === 0) input.checked = true;

      const label = document.createElement('label');
      label.className = 'form-label swatch-label';
      label.setAttribute('for', input.id);
      label.setAttribute('title', value); // Add tooltip with color name

      // Get background color/image using the comprehensive color map
      const colorStyle = getSwatchColor(value);
      label.style.cssText = 'min-width: 30px; min-height: 30px; border-radius: 50%; cursor: pointer; position: relative; border: 2px solid var(--pe-border-color); ' + colorStyle;

      if (valueIndex === 0) {
        label.style.borderColor = 'var(--pe-color-primary)';
      }

      li.appendChild(input);
      li.appendChild(label);
      ul.appendChild(li);
    });

    fieldset.appendChild(ul);
  }
  
  // Create standard options (for other types)
  function createStandardOptions(option, optionIndex, fieldset) {
    const ul = document.createElement('ul');
    ul.className = 'list-unstyled d-flex flex-wrap my-0 py-0 gap-2';
    ul.style.cssText = 'list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 8px;';
    
    option.values.forEach((value, valueIndex) => {
      const li = createSizeButton(value, optionIndex, valueIndex, option.values);
      ul.appendChild(li);
    });
    
    fieldset.appendChild(ul);
  }
  
  // Setup variant change logic (matching product page behavior)
  function setupVariantChangeLogic(productData, container) {
    const variantInputs = container.querySelectorAll('input.variant_selector');
    const variantIdInput = container.querySelector('#selected-variant-id');
    const priceElement = container.querySelector('#variant-price-display');
    const addToCartBtn = container.querySelector('#variant-add-to-cart');
    
    function updateSelectedVariant() {
      const selectedOptions = [];
      
      // Get selected values for each option
      productData.options.forEach((option, index) => {
        // First try to find a checked radio button
        let selectedInput = container.querySelector('input[name="option-' + index + '"]:checked');

        // If no checked radio button found, look for hidden input (single-value options)
        if (!selectedInput) {
          selectedInput = container.querySelector('input[name="option-' + index + '"][type="hidden"]');
        }

        if (selectedInput) {
          selectedOptions[index] = selectedInput.value;
        }
      });

      // Find matching variant using case-insensitive matching
      const selectedVariant = findMatchingVariant(productData, selectedOptions);
      
      if (selectedVariant) {
        variantIdInput.value = selectedVariant.id;

        // Update hidden price element
        const newPrice = window.promoVariantFunctions.formatMoney(selectedVariant.price);
        priceElement.textContent = newPrice;
        
        // Update button with price
        addToCartBtn.disabled = !selectedVariant.available;
        addToCartBtn.textContent = selectedVariant.available ? 'Add to Cart - ' + newPrice : 'Sold Out';
        addToCartBtn.style.background = selectedVariant.available ? 'var(--pe-color-primary)' : 'var(--pe-color-gray-400)';
        addToCartBtn.style.cursor = selectedVariant.available ? 'pointer' : 'not-allowed';
      }
    }
    
    // Add event listeners to variant inputs (matching swatch behavior)
    variantInputs.forEach(input => {
      input.addEventListener('change', function() {
        // Update visual state of swatch buttons in the same group
        const allLabelsInGroup = container.querySelectorAll('input[name="' + this.name + '"] + .swatch-label');
        allLabelsInGroup.forEach(label => {
          // Reset to default state
          if (label.closest('.color-swatch')) {
            // Color swatch styling
            label.style.borderColor = 'var(--pe-border-color)';
          } else {
            // Size/other swatch styling  
            label.style.borderColor = 'var(--pe-border-color)';
            label.style.backgroundColor = 'var(--pe-color-white)';
            label.style.color = 'var(--pe-body-color)';
          }
        });
        
        // Style the selected option
        const selectedLabel = this.nextElementSibling;
        if (selectedLabel.closest('.color-swatch')) {
          // Color swatch selected state
          selectedLabel.style.borderColor = 'var(--pe-color-primary)';
        } else {
          // Size/other swatch selected state
          selectedLabel.style.borderColor = 'var(--pe-color-primary)';
          selectedLabel.style.backgroundColor = 'var(--pe-color-primary)';
          selectedLabel.style.color = 'var(--pe-color-white)';
        }
        
        // Update variant and selected option display
        updateSelectedVariant();
      });
    });
    
    // Initial update
    updateSelectedVariant();
  }
  
  // Add selected variant to cart
  function addSelectedVariantToCart(variantId) {
    if (!variantId) return;
    
    const addToCartBtn = document.getElementById('variant-add-to-cart');
    if (addToCartBtn) {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = 'Adding...';
    }
    
    const formData = {
      items: [{ id: variantId, quantity: 1 }],
      sections: 'component-cart-drawer,header'
    };
    
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(dataJSON => {
      if (dataJSON.errors) {
        if (window.notificationEle) {
          window.notificationEle.updateNotification('Add to Cart', dataJSON.errors, { type: 'error', timeout: 5000 });
        }
        return;
      }
      sessionStorage.removeItem("NumberOnBack");
      document.querySelectorAll("[data-personalization-input]").forEach?.(el => el.value = "");
            BundleProduct.getEligibleBundleProductId().then(async (result) => {
          console.log(result, "result=================>>>>>>>>>>>>><<<<<<<<<");

          if (result) {
            const parentId = await BundleProduct.getCorrectCompleteBundleParent(
              result
            );

            if (parentId) {
              console.log("Final Parent Bundle to process:", parentId);
            }
          } else {
            console.log(" No matching product found");
          }
        });
      window.hideSimpleVariantSelection();
      
      if (window.eventBus && window.PUB_SUB_EVENTS) {
        eventBus.publish(PUB_SUB_EVENTS.productATC, { data: { item: dataJSON.items[0] } });
      }
      
      const cartElement = document.querySelector('cart-form');
      if (cartElement && typeof cartElement._updateCart === 'function') {
        cartElement._updateCart(dataJSON.sections);
      } else if (window.CartDrawer && typeof window.CartDrawer.refresh === 'function') {
        window.CartDrawer.refresh();
      }

      // Scroll to top using smart scroll manager
      CartScrollManager.scrollToTop();
    })
    .catch(() => {
      if (window.notificationEle) {
        window.notificationEle.updateNotification('Error', 'Failed to add product to cart', { type: 'error', timeout: 5000 });
      }
    })
    .finally(() => {
      if (addToCartBtn) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = 'Add to Cart';
      }
    });
  }
  
  // Global function to smoothly hide variant selection
  window.hideSimpleVariantSelection = function() {
    const cartDrawer = document.querySelector('.cart-drawer') || document.querySelector('[data-cart-form]') || document.body;
    const currentScrollPos = cartDrawer.scrollTop;
    
    // Smoothly hide the variant container
    if (window.currentVariantContainer) {
      window.currentVariantContainer.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      window.currentVariantContainer.style.opacity = '0';
      window.currentVariantContainer.style.transform = 'translateY(20px)';
      
      setTimeout(() => {
        if (window.currentVariantContainer) {
          window.currentVariantContainer.remove();
          window.currentVariantContainer = null;
        }
      }, 300);
    }
    
    // Smoothly restore the promo content (featured card stays visible)
    if (window.currentLiveBanner) {
      const scrollerNav = window.currentLiveBanner.querySelector('.promo-scroller-nav-controls');
      const productsScroll = window.currentLiveBanner.querySelector('.promo-products-scroll');
      
      // Reset any existing transitions and show elements
      if (scrollerNav) {
        scrollerNav.style.display = '';
        scrollerNav.style.opacity = '0';
        scrollerNav.style.transform = 'translateY(-10px)';
        scrollerNav.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
        
        setTimeout(() => {
          scrollerNav.style.opacity = '1';
          scrollerNav.style.transform = 'translateY(0)';
        }, 200);
      }
      
      if (productsScroll) {
        productsScroll.style.display = '';
        productsScroll.style.opacity = '0';
        productsScroll.style.transform = 'translateY(-10px)';
        productsScroll.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
        
        setTimeout(() => {
          productsScroll.style.opacity = '1';
          productsScroll.style.transform = 'translateY(0)';
        }, 250);
      }
    }
    
    // Restore scroll position after transitions
    setTimeout(() => cartDrawer.scrollTop = currentScrollPos, 100);
  };
};

// Alternative: Add reinitialization support with cleanup
window.reinitPromoScroller = function() {
  // Clean up existing initialization
  window.cleanupPromoScroller();

  // Reinitialize
  window.initPromoScroller();
};

window.cleanupPromoScroller = function() {
  // Remove all tracked event listeners
  if (window.promoScrollerCleanup && window.promoScrollerCleanup.handlers) {
    window.promoScrollerCleanup.handlers.forEach(({ element, event, handler, options }) => {
      try {
        element.removeEventListener(event, handler, options);
      } catch (e) {
        // Silently handle errors
      }
    });

    // Clear the registry
    window.promoScrollerCleanup.handlers = [];
    window.promoScrollerCleanup.elements = [];
  }

  // Remove initialization attribute from all promo containers
  const promoContainers = document.querySelectorAll('.cart-drawer-promo-banner');
  promoContainers.forEach(container => {
    container.removeAttribute('data-initialized');
  });
};

// Listen for cart drawer refresh events
if (window.eventBus && window.PUB_SUB_EVENTS) {
  // Hook into cart update events to reinitialize the scroller
  eventBus.subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    setTimeout(() => {
      window.reinitPromoScroller();
    }, 100);
  });
}

// Listen for custom cart drawer refresh events
document.addEventListener('cart:refresh', () => {
  setTimeout(() => {
    window.reinitPromoScroller();
  }, 100);
});

// Watch for cart drawer DOM changes (when Shopify replaces the HTML)
function setupCartDrawerObserver() {
  const cartDrawer = document.querySelector('#component-cart-drawer') ||
                     document.querySelector('.cart-drawer') ||
                     document.querySelector('[data-cart-form]');

  if (cartDrawer && !window.cartDrawerObserver) {
    window.cartDrawerObserver = new MutationObserver((mutations) => {
      // Check if promo banner was added/changed
      const hasPromoBanner = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType === 1) { // Element node
            return node.classList?.contains('cart-drawer-promo-banner') ||
                   node.querySelector?.('.cart-drawer-promo-banner');
          }
          return false;
        });
      });

      if (hasPromoBanner) {
        setTimeout(() => {
          window.reinitPromoScroller();
        }, 150);
      }
    });

    window.cartDrawerObserver.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.initPromoScroller();
    setupCartDrawerObserver();
  });
} else {
  window.initPromoScroller();
  setupCartDrawerObserver();
} 